import { useQueryClient } from '@tanstack/react-query';
import { Download, Upload } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { ApiError, keys, useMeta } from '@/api';
import { AmbientSettingsCard } from '@/components/Ambient';
import { AuditCard } from '@/components/Audit';
import { DatasetsCard } from '@/components/Datasets';
import { Button, Card, Field, PageHeader, Select } from '@/components/ui';
import { SPACE } from '@/lib/space';

export function SettingsPage() {
	const meta = useMeta();
	const qc = useQueryClient();
	const [mode, setMode] = useState<'merge' | 'replace'>('merge');
	const [busy, setBusy] = useState(false);

	const upload = async (file: File) => {
		if (mode === 'replace' && !confirm("Replace this space's whole fleet with this file? Its sensors not in the file are deleted.")) return;
		setBusy(true);
		try {
			const res = await fetch(`/api/v1/import?mode=${mode}`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/yaml', 'X-Requested-With': 'mock-sensors', 'x-mock-space': SPACE },
				body: await file.text(),
			});
			const body = await res.json();
			if (!res.ok) throw new ApiError(res.status, body);
			toast.success(`Imported: ${body.created} created, ${body.updated} updated, ${body.deleted} deleted`);
			qc.invalidateQueries({ queryKey: keys.instances });
			qc.invalidateQueries({ queryKey: keys.status });
		} catch (e) {
			const err = e as ApiError;
			const first = err.problem?.errors?.[0];
			toast.error(first ? `${first.path}: ${first.message}` : err.message);
		} finally {
			setBusy(false);
		}
	};

	return (
		<>
			<PageHeader title="Settings" />
			<div className="grid gap-4 lg:grid-cols-2">
				<Card title="Export fleet">
					<p className="mb-3 text-sm text-fg-muted">Every sensor with its parameters, faults and bindings as YAML. Use it as a backup or as a seed file (MOCK_SEED_FILE).</p>
					{/* A download link cannot send the space header: it goes in the query. */}
					<a href={`/api/v1/export?space=${SPACE}`} download>
						<Button>
							<Download className="size-4" /> Download YAML
						</Button>
					</a>
				</Card>
				<Card title="Import fleet">
					<div className="flex flex-col gap-3">
						<Field label="Mode" hint={mode === 'merge' ? 'Sensors with the same slug are replaced; others are kept.' : 'The fleet becomes exactly what the file contains.'}>
							<Select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
								<option value="merge">Merge by slug</option>
								<option value="replace">Replace everything</option>
							</Select>
						</Field>
						<label>
							<input type="file" accept=".yaml,.yml,application/yaml" className="hidden" disabled={busy} onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
							<span className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-border bg-surface px-3.5 text-sm font-medium hover:bg-muted">
								<Upload className="size-4" /> Choose YAML file…
							</span>
						</label>
					</div>
				</Card>
				<Card title="Limits">
					{meta.data && (
						<dl className="grid grid-cols-2 gap-y-2 text-sm">
							<dt className="text-fg-muted">Max sensors</dt>
							<dd className="font-mono">{meta.data.limits.maxInstances}</dd>
							<dt className="text-fg-muted">Fastest interval</dt>
							<dd className="font-mono">{meta.data.limits.minIntervalMs} ms</dd>
							<dt className="text-fg-muted">Sample budget</dt>
							<dd className="font-mono">{meta.data.limits.maxSamplesPerSec}/s</dd>
							<dt className="text-fg-muted">Bulk create</dt>
							<dd className="font-mono">{meta.data.limits.maxBulk}</dd>
						</dl>
					)}
					<p className="mt-3 text-xs text-fg-muted">Set with MOCK_* environment variables on the server.</p>
				</Card>
				<Card title="API">
					<p className="text-sm text-fg-muted">
						OpenAPI document: <a className="text-accent hover:underline" href="/openapi.json">/openapi.json</a>. Public read-only data:{' '}
						<a className="text-accent hover:underline" href={`/data/v1/${SPACE}/sensors`}>
							/data/v1/{SPACE}/sensors
						</a>
						.
					</p>
				</Card>
				{SPACE === 'ambient' && <AmbientSettingsCard />}
				<DatasetsCard />
				<AuditCard />
			</div>
		</>
	);
}
